// Rows on the line-up editors (the board; shared/pvpfight.ts): a Front / Back toggle and a melee or ranged badge per
// creature, and the front count. An editor keeps its rows by creature id and starts from the line-up's saved rows, else
// from defaultRows as the fight would place it; it sends rows only once they were set by hand (a line-up saved without
// rows keeps following defaultRows as its creatures grow). Hidden while the board is off.
import { BOARD, reachOf, type BoardRow } from '../shared/board.ts';
import { AWAKENED_CD_MS, WAVE_MIN_TARGETS, WAVE_SPLASH } from '../shared/moves.ts';
import { BOARD_SWITCH, frontCount, rowsShown } from '../shared/pvpfight.ts';
import { SYNERGY_SWITCH, type SynergyOpts } from '../shared/synergy.ts';
import type { ArenaCreature } from '../shared/arena.ts';
import { esc } from './fmt.ts';

/** An editor's rows by creature id, or null while untouched (the line-up stands on defaultRows). */
export type RowPick = Record<number, BoardRow> | null;

export const boardOn = () => BOARD_SWITCH.on;

/** A pick from saved rows (rows[k] is ids[k]'s), or null when none were saved. */
export function pickFrom(ids: readonly number[], rows?: readonly BoardRow[] | null): RowPick {
  if (!rows || rows.length !== ids.length) return null;
  return Object.fromEntries(ids.map((id, k) => [id, rows[k]]));
}

/** The rows `team` stands on: the pick (unset: back), else defaultRows with synergies as live (or `syn`). */
export function shownRows(team: readonly ArenaCreature[], pick: RowPick, syn?: SynergyOpts | null): BoardRow[] {
  const s = syn !== undefined ? syn : SYNERGY_SWITCH.on ? {} : null;
  return rowsShown(team, pick ? team.map((c) => pick[c.id]) : null, s);
}

/** Set creature `id` to `row` (the pick starts from what shows), unless that would put more than BOARD.frontMax in front. */
export function setRow(team: readonly ArenaCreature[], pick: RowPick, id: number, row: BoardRow, syn?: SynergyOpts | null): RowPick {
  const shown = shownRows(team, pick, syn);
  // Creatures that don't fight now (above your level) keep what they had.
  const next: Record<number, BoardRow> = { ...(pick ?? {}), ...Object.fromEntries(team.map((c, k) => [c.id, shown[k]])) };
  if (row === 'front' && next[id] !== 'front' && frontCount(Object.values(next)) >= BOARD.frontMax) return pick;
  next[id] = row;
  return next;
}

/**
 * Rows to send with `ids`: undefined while untouched (the line-up keeps following defaultRows), else one per id as the
 * editor shows it (`shown`, by id), then the pick, then the back.
 */
export const rowsOut = (ids: readonly number[], pick: RowPick, shown?: ReadonlyMap<number, BoardRow>): BoardRow[] | undefined =>
  (pick ? ids.map((id) => shown?.get(id) ?? pick[id] ?? 'back') : undefined);

/** The creature's reach: melee hits the front row first, ranged picks anyone. */
export function reachBadge(species: string): string {
  if (!boardOn()) return '';
  const r = reachOf(species);
  return `<span class="rw-reach ${r}" title="${r === 'melee' ? 'Melee: hits the front row first' : 'Ranged: hits any row'}">${r === 'melee' ? 'Melee' : 'Ranged'}</span>`;
}

/**
 * A Front / Back toggle for one creature: data-act=`act`, data-id="<id>:front" or "<id>:back". `full`: the front row
 * already holds BOARD.frontMax, so Front is shut for a creature at the back. attrs: more attributes for both buttons.
 */
export function rowToggle(act: string, id: number, row: BoardRow, full: boolean, name = '', attrs = ''): string {
  if (!boardOn()) return '';
  const b = (r: BoardRow, label: string) => `<button class="rw-o${row === r ? ' on' : ''}" data-act="${act}" data-id="${id}:${r}"${attrs} aria-pressed="${row === r}"${r === 'front' && row !== 'front' && full ? ' disabled' : ''}>${label}</button>`;
  return `<span class="rw-tg" role="group" aria-label="${name ? `${esc(name)}: row` : 'Row'}">${b('front', 'Front')}${b('back', 'Back')}</span>`;
}

/** "Front 2/3" for an editor's head. */
export function frontLabel(rows: readonly BoardRow[]): string {
  if (!boardOn()) return '';
  const n = frontCount(rows);
  return `<span class="rw-count${n >= BOARD.frontMax ? ' full' : ''}">Front ${n}/${BOARD.frontMax}</span>`;
}

/** Parse a toggle's data-id. */
export function rowClick(id: string): { id: number; row: BoardRow } | null {
  const [a, b] = id.split(':');
  return (b === 'front' || b === 'back') && Number(a) ? { id: Number(a), row: b } : null;
}

/** Help page section, next to the Arena's: the board's rules and numbers (shared/board.ts). Only while it is on. */
export function boardHelp(): string {
  if (!boardOn()) return '';
  const c = BOARD.charge;
  return `<h4>The board</h4><p>Every PvP battle is fought on two rows a side, all creatures at once: the Arena, Element Halls, friendly and training battles, practice runs, tournaments and Trail trainers. Never Guild War, hunts or bosses.</p>
    <ul><li><b>Rows</b>: each creature stands in the front or the back row, at most ${BOARD.frontMax} in front. Place them on the battle stage: drag a creature to a cell, or tap it and then a cell (the Halls and training editors use Front and Back). A line-up you never set puts its bulkiest creatures in front. You see the other side's rows only when the fight starts.</li>
      <li><b>Reach</b>: melee creatures (they hit with Attack) hit the front row while anyone stands in it, the lowest HP first, then the back row. Ranged creatures (they hit with Magic) hit any row, picking the creature their best ready move hits hardest.</li>
      <li><b>Charge bar</b>: the thin bar under the HP bar. Each move a creature casts adds ${c.perBlow}; each blow it takes adds ${c.perHpTaken / 100} for every 1% of its HP lost (at most ${c.hitCap} a blow). Full at ${c.max}, it fires the creature's Signature and empties (an Awakened form fires its Awakened move first${BOARD.awakened === 'cooldown' ? `, when its ${AWAKENED_CD_MS / 1000} s cooldown allows,` : ''} then its Signature). A creature without one shows no bar.</li>
      <li><b>Waves</b> hit a whole row at ×${WAVE_SPLASH} damage: melee the front row while it stands, ranged the fuller row. A Wave waits until its row holds at least ${WAVE_MIN_TARGETS} creatures.</li>
      ${Object.keys(BOARD.synergyScale).length || Object.keys(BOARD.heldScale).length ? '<li><b>Synergies and held items</b> count for less on the board (Speed and Kin most), since every creature fights at once. Chips, items and this Help show the board\'s numbers.</li>' : ''}
      <li><b>Time</b>: HP counts ×${BOARD.hpMult}. From ${BOARD.overtimeMs / 1000} s all damage doubles every ${BOARD.overtimeDoubleMs / 1000} s; at ${BOARD.capMs / 1000} s the side with more creatures standing wins, then the larger share of HP left, then the defender.</li></ul>`;
}
